export type FacebookMetricDirection = "up" | "down" | "flat";

export interface FacebookStatCard {
  id: string;
  title: string;
  value: number | string;
  unit?: "percent";
  direction?: FacebookMetricDirection;
  description?: string;
}

export interface FacebookAudienceSegment {
  label: string;
  value: number | string;
  detail?: string;
}

export interface FacebookPostMetric {
  id: string;
  caption: string;
  typeLabel: string;
  dateLabel: string;
  timeLabel: string;
  reach: number;
  views: number;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  status?: string;
}

export interface FacebookTrendPoint {
  label: string;
  followers: number;
  views: number;
  pageViews: number;
  posts: number;
  reactions: number;
  clicks: number;
}

export interface FacebookAnalyticsSnapshot {
  accountName: string;
  communityName: string;
  periodLabel: string;
  updatedAtLabel: string;
  metaNotice: string[];
  growth: {
    summary: FacebookStatCard[];
    cadence: FacebookStatCard[];
    trend: FacebookTrendPoint[];
  };
  followerBalance: FacebookStatCard[];
  viewedPosts: FacebookStatCard[];
  demographics: {
    countries: FacebookAudienceSegment[];
    cities: FacebookAudienceSegment[];
  };
  clicks: {
    summary: FacebookStatCard[];
    types: FacebookAudienceSegment[];
  };
  publications: {
    summary: FacebookStatCard[];
    interactions: FacebookStatCard[];
    rates: FacebookStatCard[];
    organicPaid: FacebookAudienceSegment[];
    posts: FacebookPostMetric[];
    chartTrend: FacebookTrendPoint[];
  };
  competitors: {
    title: string;
    message: string;
    hint: string;
    filters: string[];
  };
}

const trend: FacebookTrendPoint[] = [
  { label: "13 abr", followers: 248, views: 32, pageViews: 5, posts: 0, reactions: 0, clicks: 0 },
  { label: "14 abr", followers: 248, views: 28, pageViews: 4, posts: 0, reactions: 0, clicks: 0 },
  { label: "15 abr", followers: 248, views: 35, pageViews: 5, posts: 0, reactions: 0, clicks: 0 },
  { label: "16 abr", followers: 249, views: 75, pageViews: 8, posts: 1, reactions: 0, clicks: 2 },
  { label: "17 abr", followers: 249, views: 48, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "18 abr", followers: 249, views: 41, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "19 abr", followers: 250, views: 59, pageViews: 7, posts: 1, reactions: 1, clicks: 1 },
  { label: "20 abr", followers: 250, views: 45, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "21 abr", followers: 250, views: 62, pageViews: 8, posts: 1, reactions: 2, clicks: 2 },
  { label: "22 abr", followers: 250, views: 40, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "23 abr", followers: 250, views: 36, pageViews: 5, posts: 0, reactions: 0, clicks: 0 },
  { label: "24 abr", followers: 250, views: 34, pageViews: 5, posts: 0, reactions: 0, clicks: 0 },
  { label: "25 abr", followers: 250, views: 56, pageViews: 7, posts: 0, reactions: 0, clicks: 0 },
  { label: "26 abr", followers: 250, views: 42, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "27 abr", followers: 250, views: 39, pageViews: 6, posts: 0, reactions: 0, clicks: 0 },
  { label: "28 abr", followers: 251, views: 43, pageViews: 7, posts: 1, reactions: 1, clicks: 1 },
  { label: "29 abr", followers: 251, views: 51, pageViews: 7, posts: 0, reactions: 0, clicks: 0 },
  { label: "30 abr", followers: 251, views: 34, pageViews: 6, posts: 1, reactions: 1, clicks: 1 },
];

export const facebookAnalyticsMock: FacebookAnalyticsSnapshot = {
  accountName: "Escuela Mi Mundo en Movimiento",
  communityName: "Escuela Mi Mundo en Movimiento",
  periodLabel: "13 abr 2026 - 30 abr 2026",
  updatedAtLabel: "06/05/2026 09:20",
  metaNotice: [
    "Los Me gusta de página desaparecerán a partir del 1 de noviembre de 2025.",
    "Balance de Me gusta se sustituye por balance de Seguidores.",
    "Impresiones de página se sustituye por Visualizaciones.",
  ],
  growth: {
    summary: [
      { id: "followers", title: "Seguidores", value: 251, direction: "up", description: "Total de seguidores" },
      { id: "views", title: "Visualizaciones", value: 823, direction: "down", description: "Visualizaciones de página" },
      { id: "pageViews", title: "Visitas a página", value: 115, direction: "down", description: "Visitas registradas" },
      { id: "content", title: "Contenido total", value: 5, direction: "down", description: "Publicaciones del periodo" },
    ],
    cadence: [
      { id: "dailyNewFollowers", title: "Promedio diario de nuevos seguidores", value: 0.06 },
      { id: "pageViewsPerDay", title: "Vistas de página al día", value: 6.39 },
      { id: "postsPerDay", title: "Publicaciones por día", value: 0.28 },
      { id: "postsPerWeek", title: "Publicaciones por semana", value: 1.94 },
    ],
    trend,
  },
  followerBalance: [
    { id: "balance", title: "Balance de Seguidores", value: 3 },
    { id: "acquired", title: "Adquiridos", value: 1, direction: "up" },
    { id: "lost", title: "Perdidos", value: 5 },
    { id: "content", title: "Contenido total", value: 5, direction: "down" },
  ],
  viewedPosts: [
    { id: "views", title: "Visualizaciones", value: 656, direction: "down" },
    { id: "reactions", title: "Reacciones", value: 5, direction: "down" },
  ],
  demographics: {
    countries: [
      { label: "Chile", value: "Principal" },
      { label: "Estados Unidos", value: "Secundario" },
    ],
    cities: [
      { label: "La Serena, Chile", value: 64.68, detail: "%" },
      { label: "Coquimbo, Chile", value: 19.44, detail: "%" },
      { label: "Santiago, Chile", value: 4.37, detail: "%" },
      { label: "Rinconada, Chile", value: 1.59, detail: "%" },
      { label: "Antofagasta", value: 1.19, detail: "%" },
    ],
  },
  clicks: {
    summary: [
      { id: "totalClicks", title: "Clics totales", value: 0 },
      { id: "pageViews", title: "Visitas a página", value: 115, direction: "down" },
      { id: "content", title: "Contenido total", value: 5, direction: "down" },
    ],
    types: [
      { label: "Visualizaciones", value: 823 },
      { label: "Clics", value: 0 },
      { label: "Visitas a página", value: 115 },
    ],
  },
  publications: {
    summary: [
      { id: "engagement", title: "Engagement", value: 7.32, unit: "percent", direction: "up" },
      { id: "interactions", title: "Interacciones", value: 12, direction: "down" },
      { id: "avgReach", title: "Promedio alcance/post", value: 33, direction: "down" },
      { id: "views", title: "Visualizaciones", value: 273, direction: "down" },
      { id: "posts", title: "Posts", value: 5 },
    ],
    interactions: [
      { id: "reactions", title: "Reacciones", value: 5, direction: "down" },
      { id: "comments", title: "Comentarios", value: "-" },
      { id: "shares", title: "Compartidos", value: "-" },
      { id: "clicks", title: "Clics", value: 7, direction: "up" },
      { id: "posts", title: "Posts", value: 5 },
    ],
    rates: [
      { id: "dailyReactions", title: "Reacciones diarias", value: 0.28 },
      { id: "reactionsPerPost", title: "Reacciones por publicación", value: 1 },
      { id: "dailyComments", title: "Comentarios diarios", value: "-" },
      { id: "commentsPerPost", title: "Comentarios por publicación", value: "-" },
      { id: "dailyShares", title: "Compartidos por día", value: "-" },
      { id: "sharesPerPost", title: "Compartidos por publicación", value: "-" },
    ],
    organicPaid: [
      { label: "Orgánicas (Visualizaciones)", value: 656 },
      { label: "De Pago (Visualizaciones)", value: 0 },
    ],
    posts: [
      {
        id: "1",
        caption: "🌍 💙 Aprender a convivir es parte del día a día. En Mi Mundo en Movimiento, las instancias...",
        typeLabel: "Imagen",
        dateLabel: "16 abr 2026",
        timeLabel: "11:45",
        reach: 42,
        views: 75,
        reactions: 0,
        comments: 0,
        shares: 0,
        clicks: 2,
        status: "Nuevo",
      },
      {
        id: "2",
        caption: "🌍 💙 En Mi Mundo en Movimiento, el juego tiene un propósito. Cada actividad responde a...",
        typeLabel: "Imagen",
        dateLabel: "21 abr 2026",
        timeLabel: "15:46",
        reach: 35,
        views: 62,
        reactions: 2,
        comments: 0,
        shares: 0,
        clicks: 2,
      },
      {
        id: "3",
        caption: "🌍 💙 Los desafíos también enseñan. En Mi Mundo en Movimiento, las actividades motric...",
        typeLabel: "Imagen",
        dateLabel: "19 abr 2026",
        timeLabel: "12:02",
        reach: 31,
        views: 59,
        reactions: 1,
        comments: 0,
        shares: 0,
        clicks: 1,
      },
      {
        id: "4",
        caption: "En Mi Mundo en Movimiento entendemos que no todos aprenden de la misma forma. Alguno...",
        typeLabel: "Imagen",
        dateLabel: "28 abr 2026",
        timeLabel: "14:05",
        reach: 29,
        views: 43,
        reactions: 1,
        comments: 0,
        shares: 0,
        clicks: 1,
      },
      {
        id: "5",
        caption: "¿Sabías que la estructura es clave en el desarrollo de niños con TEA? Los entornos...",
        typeLabel: "Imagen",
        dateLabel: "30 abr 2026",
        timeLabel: "10:04",
        reach: 27,
        views: 34,
        reactions: 1,
        comments: 0,
        shares: 0,
        clicks: 1,
      },
    ],
    chartTrend: trend,
  },
  competitors: {
    title: "Lista de competidores",
    message: "¡Vaya! No hemos encontrado nada",
    hint: "Utiliza los filtros para acotar tu búsqueda. Comprueba el rango de fechas en el que quieres buscar.",
    filters: ["Publicaciones (Todos)", "Todos los competidores"],
  },
};
